import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';

/**
 * Hostname where the FullStory snippet is injected (see `index.html`).
 * Keep in sync with that guard — service calls must not run anywhere else.
 */
export const FULLSTORY_PROD_HOSTNAME = 'carwash.way.com';

/** FullStory browser snippet API (loaded only on carwash.way.com). */
export interface FullStoryApi {
  identify: (uid: string | false, userVars?: Record<string, any>) => void;
  setUserVars: (userVars: Record<string, any>) => void;
  anonymize: () => void;
  event: (name: string, properties?: Record<string, any>) => void;
  setVars: (type: string, properties: Record<string, any>) => void;
}

declare global {
  interface Window {
    FS?: FullStoryApi;
  }
}

export interface FullStoryIdentifyInput {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  shopName?: string | null;
}

export interface FullStoryLicensePlateInput {
  plate: string;
  state?: string | null;
  make?: string | null;
  model?: string | null;
  source?: string;
}

/**
 * Thin FullStory wrapper. Recording is production-only:
 * build `envName === 'production'` and hostname `carwash.way.com`.
 * All methods no-op when FS is absent or the environment is not prod.
 */
@Injectable({
  providedIn: 'root',
})
export class FullStoryService {
  private lastPlateKey: string | null = null;
  private plateTrackTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingPlate: FullStoryLicensePlateInput | null = null;

  constructor(private readonly logger: LoggerService) {}

  /**
   * True only for production builds on the prod hostname (matches `index.html` snippet).
   * Exposed for tests.
   */
  isRecordingAllowed(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (environment.envName !== 'production') {
      return false;
    }
    return window.location.hostname === FULLSTORY_PROD_HOSTNAME;
  }

  /** Resolves the global FS API when recording is allowed and the snippet is present. */
  getFsApi(): FullStoryApi | undefined {
    if (!this.isRecordingAllowed()) {
      return undefined;
    }
    return window.FS;
  }

  /**
   * Identify the current operator session. Call on login and when restoring a session from storage.
   */
  identifyUser(input: FullStoryIdentifyInput): void {
    const fs = this.getFsApi();
    if (!fs?.identify) {
      return;
    }

    const uid = String(input.uid || '').trim();
    if (!uid) {
      return;
    }

    const userVars: Record<string, string> = {};
    const email = String(input.email || '').trim();
    const displayName = String(input.displayName || '').trim();
    const shopName = String(input.shopName || '').trim();

    if (email) {
      userVars['email'] = email;
    }
    if (displayName) {
      userVars['displayName'] = displayName;
    }
    if (shopName) {
      // Typed suffix so FullStory indexes the property for search/filters.
      userVars['shopName_str'] = shopName;
    }

    try {
      fs.identify(uid, userVars);
      this.logger.debug('FullStory identify', { uid, email: email || undefined, shopName: shopName || undefined });
    } catch (error) {
      this.logger.error('FullStory identify failed', error);
    }
  }

  /** Attach or update shop name after the shop profile loads. */
  setShopName(shopName: string | null | undefined): void {
    const fs = this.getFsApi();
    const name = String(shopName || '').trim();
    if (!fs?.setUserVars || !name) {
      return;
    }

    try {
      fs.setUserVars({ shopName_str: name });
    } catch (error) {
      this.logger.error('FullStory setShopName failed', error);
    }
  }

  /**
   * Clear session user metadata on logout. Resets plate dedupe so a later login starts clean.
   */
  clearSession(): void {
    this.clearPendingPlateTrack();
    this.lastPlateKey = null;

    const fs = this.getFsApi();
    if (!fs?.anonymize) {
      return;
    }

    try {
      fs.anonymize();
      this.logger.debug('FullStory session cleared');
    } catch (error) {
      this.logger.error('FullStory anonymize failed', error);
    }
  }

  /**
   * Record a license-plate marker on the session timeline (searchable + jumpable).
   * Debounced so typing does not flood events; pass `immediate: true` for LPR/autofill.
   */
  trackLicensePlate(input: FullStoryLicensePlateInput, options?: { immediate?: boolean }): void {
    if (!this.isRecordingAllowed()) {
      return;
    }

    const plate = String(input.plate || '')
      .trim()
      .toUpperCase();
    if (!plate) {
      return;
    }

    this.pendingPlate = {
      ...input,
      plate,
      state: input.state ? String(input.state).trim().toUpperCase() : undefined,
      make: input.make ? String(input.make).trim() : undefined,
      model: input.model ? String(input.model).trim() : undefined,
    };

    if (options?.immediate) {
      this.flushPendingPlateTrack();
      return;
    }

    this.clearPendingPlateTrack(false);
    this.plateTrackTimer = setTimeout(() => this.flushPendingPlateTrack(), 600);
  }

  private flushPendingPlateTrack(): void {
    this.clearPendingPlateTrack(false);
    const input = this.pendingPlate;
    this.pendingPlate = null;
    if (!input) {
      return;
    }

    const state = input.state || '';
    const plateKey = `${state}|${input.plate}`;
    if (plateKey === this.lastPlateKey) {
      return;
    }
    this.lastPlateKey = plateKey;

    const fs = this.getFsApi();
    if (!fs) {
      return;
    }

    const properties: Record<string, string> = {
      licensePlate_str: input.plate,
    };
    if (state) {
      properties['licensePlateState_str'] = state;
    }
    if (input.make) {
      properties['vehicleMake_str'] = input.make;
    }
    if (input.model) {
      properties['vehicleModel_str'] = input.model;
    }
    if (input.source) {
      properties['source_str'] = input.source;
    }

    try {
      if (fs.event) {
        fs.event('License Plate', properties);
      }
      if (fs.setVars) {
        fs.setVars('page', properties);
      }
    } catch (error) {
      this.logger.error('FullStory trackLicensePlate failed', error);
    }
  }

  private clearPendingPlateTrack(clearPending = true): void {
    if (this.plateTrackTimer != null) {
      clearTimeout(this.plateTrackTimer);
      this.plateTrackTimer = null;
    }
    if (clearPending) {
      this.pendingPlate = null;
    }
  }
}
